import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserService } from './user-services/user.service';
import { SubscriptionAccessService } from './subscription-access.service';

export interface Permission {
  module: string;
  add: boolean;
  view: boolean;
  delete: boolean;
  update: boolean;
}

export interface UserPermissions {
  [module: string]: {
    add: boolean;
    view: boolean;
    delete: boolean;
    update: boolean;
  };
}

@Injectable({
  providedIn: 'root'
})
export class RbacService {
  // Road domain orgs get full access to these regardless of role permissions
  private static readonly ROAD_GEOSPATIAL_MODULES = new Set([
    'gis',
    'gis assets',
    'geospatial',
    'map',
    'map editor',
    'road boq',
    'segment inspection',
    'cad viewer',
  ]);

  private userPermissions$ = new BehaviorSubject<UserPermissions | null>(null);
  private currentUserRole$ = new BehaviorSubject<any>(null);

  constructor(private userService: UserService, private subscriptionAccess: SubscriptionAccessService) {
    this.loadUserPermissions();
  }

  /**
   * Load user permissions from session/localStorage
   */
  loadUserPermissions(): void {
    try {
      const userData = this.userService.getUserDetailsFromSession();
      if (!userData) {
        this.userPermissions$.next(null);
        return;
      }

      // Handle nested user structure
      const actualUser = userData.user || userData;
      
      // Get role info
      const role = actualUser.role_id || actualUser.role;
      const roleName = actualUser.role_name || actualUser.role;
      
      this.currentUserRole$.next({
        role_id: role,
        role_name: roleName,
        permissions: actualUser.permissions
      });

      // Parse permissions
      let permissions: UserPermissions = {};
      
      if (actualUser.permissions) {
        try {
          const perms = typeof actualUser.permissions === 'string' 
            ? JSON.parse(actualUser.permissions) 
            : actualUser.permissions;
          
          if (Array.isArray(perms)) {
            // Convert array format to object format
            perms.forEach((perm: Permission) => {
              permissions[perm.module.toLowerCase()] = {
                add: perm.add || false,
                view: perm.view || false,
                delete: perm.delete || false,
                update: perm.update || false
              };
            });
          } else if (typeof perms === 'object') {
            permissions = perms;
          }
        } catch (e) {
          console.error('Error parsing permissions:', e);
        }
      }

      // Admin always has full access (role_name casing varies in DB / API)
      const rn = typeof roleName === 'string' ? roleName.toLowerCase() : '';
      if (actualUser.type === 1 || rn === 'admin' || rn === 'super admin') {
        permissions = this.getFullPermissions();
      }

      this.userPermissions$.next(permissions);
    } catch (error) {
      console.error('Error loading user permissions:', error);
      this.userPermissions$.next(null);
    }
  }

  /**
   * Get full permissions (for admin users)
   */
  private getFullPermissions(): UserPermissions {
    return {
      admin: { add: true, view: true, delete: true, update: true },
      user: { add: true, view: true, delete: true, update: true },
      machine: { add: true, view: true, delete: true, update: true },
      dashboard: { add: true, view: true, delete: true, update: true },
      leads: { add: true, view: true, delete: true, update: true },
      gis: { add: true, view: true, delete: true, update: true },
      'gis assets': { add: true, view: true, delete: true, update: true },
      geospatial: { add: true, view: true, delete: true, update: true },
      integrations: { add: true, view: true, delete: true, update: true },
      // 'road boq': { add: true, view: true, delete: true, update: true },
      // 'cad viewer': { add: true, view: true, delete: true, update: true },
      'work breakdown structure': { add: true, view: true, delete: true, update: true },
      attendance: { add: true, view: true, delete: true, update: true },
      'project binding': { add: true, view: true, delete: true, update: true },
      'project award': { add: true, view: true, delete: true, update: true },
      'support ticket': { add: true, view: true, delete: true, update: true },
      'staff list': { add: true, view: true, delete: true, update: true },
      projects: { add: true, view: true, delete: true, update: true },
      bom: { add: true, view: true, delete: true, update: true },
      boq: { add: true, view: true, delete: true, update: true },
      bills: { add: true, view: true, delete: true, update: true },
      financial: { add: true, view: true, delete: true, update: true },
      documents: { add: true, view: true, delete: true, update: true },
      alerts: { add: true, view: true, delete: true, update: true }
    };
  }

  /**
   * Check if user has permission for a module and action
   * Returns true if:
   * - User is admin
   * - No RBAC is configured (permissions not set) - allows access based on subscription/role
   * - Permission is explicitly granted
   */
  hasPermission(module: string, action: 'add' | 'view' | 'delete' | 'update' | 'all' = 'view'): boolean {
    // Check if user is admin first
    if (this.isAdmin()) {
      return true;
    }

    if (RbacService.ROAD_GEOSPATIAL_MODULES.has(module.toLowerCase()) && this.subscriptionAccess.hasRoadDomainSubscription()) {
      return true;
    }

    const permissions = this.userPermissions$.value;
    
    // If no permissions are configured, allow access (rely on subscription/role checks)
    // This allows the system to work without RBAC if permissions aren't set
    if (!permissions || Object.keys(permissions).length === 0) {
      return true;
    }

    const modulePerms = permissions[module.toLowerCase()];
    
    // If specific module not found in permissions, allow access
    // This means permissions are configured but this module isn't restricted
    if (!modulePerms) {
      return true;
    }

    if (action === 'all') {
      return modulePerms.view || modulePerms.add || modulePerms.update || modulePerms.delete;
    }

    return modulePerms[action] || false;
  }

  /**
   * Check if user can access a route/module
   */
  canAccess(module: string): boolean {
    return this.hasPermission(module, 'view');
  }

  /**
   * Get current user permissions as observable
   */
  getUserPermissions(): Observable<UserPermissions | null> {
    return this.userPermissions$.asObservable();
  }

  /**
   * Get current user permissions synchronously
   */
  getCurrentPermissions(): UserPermissions | null {
    return this.userPermissions$.value;
  }

  /**
   * Get current user role
   */
  getCurrentRole(): Observable<any> {
    return this.currentUserRole$.asObservable();
  }

  /**
   * Check if user is admin
   */
  isAdmin(): boolean {
    const userData = this.userService.getUserDetailsFromSession();
    if (!userData) return false;
    
    const actualUser = userData.user || userData;
    const rn = typeof actualUser.role_name === 'string' ? actualUser.role_name.toLowerCase() : '';
    return actualUser.type === 1 || rn === 'admin' || rn === 'super admin';
  }

  /**
   * Refresh permissions (useful after role update)
   */
  refreshPermissions(): void {
    this.loadUserPermissions();
  }

  /**
   * Map route to module name for permission checking
   * Comprehensive mapping of all routes to permission modules
   */
  routeToModule(route: string): string {
    const routeMap: { [key: string]: string } = {
      // Core & Dashboard
      '/dashboard': 'dashboard',
      
      // Administration
      '/role-list': 'role and permission',
      '/add-role-and-permission': 'role and permission',
      '/edit-role-and-permission': 'role and permission',
      '/services-list': 'master',
      '/notification': 'notification',
      '/portal-builder': 'portal builder',
      '/package-list': 'package list',
      '/asset-management': 'gis assets',
      '/gis-mvp': 'gis assets',
      '/home/gis-mvp': 'gis assets',
      
      // Organization Management
      '/company': 'company',
      '/organization-branch-list': 'branch',
      '/organization-add-branch': 'branch',
      '/organization-edit-branch': 'branch',
      '/multi-organization': 'organization',
      '/edit-organizations': 'organization',
      '/edit-organization': 'organization',
      '/subscription': 'subscription',
      
      // User & Staff Management
      '/user': 'user',
      '/add-user': 'user',
      '/edit-user': 'user',
      '/employees': 'employees',
      '/staff': 'staff',
      '/staff/edit': 'staff',
      '/attendance': 'attendance',
      
      // Projects (all stages)
      '/projects': 'projects',
      '/add-project': 'projects',
      '/edit-project': 'projects',
      '/view-project': 'projects',
      '/pre-planning-projects': 'pre-planning',
      '/add-pre-planning-project': 'pre-planning',
      '/edit-pre-planning-project': 'pre-planning',
      '/planning-stage-list': 'planning',
      '/add-planning-stage': 'planning',
      '/edit-planning-stage': 'planning',
      '/design-stage-list': 'design',
      '/design-stage': 'design',
      '/edit-design-stage': 'design',
      '/estimation-stage-list': 'estimation',
      '/add-estimation-stage': 'estimation',
      '/edit-estimation-stage': 'estimation',
      '/biding-list': 'bidding',
      '/add-biding': 'bidding',
      '/project-biding': 'bidding',
      '/award-list': 'project award',
      '/add-award': 'project award',
      '/purchase-order-list': 'purchase order',
      '/add-purchase-order': 'purchase order',
      '/purchase-order': 'purchase order',
      '/received-purchase-orders': 'purchase order',
      '/work-orders': 'work order',
      '/work-orders/new': 'work order',
      '/work-orders/:id': 'work order',
      '/work-orders/:id/edit': 'work order',
      '/survey-list': 'survey',
      '/add-survey': 'survey',
      '/edit-survey': 'survey',
      '/edit-survey/:id': 'survey',
      '/execution-list': 'execution',
      '/add-execution': 'execution',
      '/deviations-report-list': 'deviations report',
      '/add-deviations-report': 'deviations report',
      '/po-amendment-list': 'po amendment',
      '/add-po-amendment': 'po amendment',
      '/siv-list': 'siv',
      '/add-siv': 'siv',
      '/srv-list': 'srv',
      '/add-srv': 'srv',
      '/inspection-stage-list': 'inspection',
      '/add-inspection-stage': 'inspection',
      '/edit-inspection-stage': 'inspection',
      '/monitoring-list': 'monitoring',
      '/add-monitoring': 'monitoring',
      '/invoice-list': 'invoice list',
      '/add-invoice': 'invoice',
      '/edit-invoice': 'invoice',
      '/closure-list': 'closure',
      '/add-closure': 'closure',
      '/project-bindding': 'project binding',
      '/project-award': 'project award',
      '/work-breakdown-structure': 'work breakdown structure',
      '/work-breakdown-structure/list': 'work breakdown structure',
      '/work-breakdown-structure/deviation-report': 'work breakdown structure',
      
      // Materials & Resources
      '/materials': 'materials',
      '/material-list': 'materials',
      '/add-material': 'materials',
      '/edit-material': 'materials',
      '/view-material': 'materials',
      '/material-cost': 'material cost',
      '/machines': 'machines',
      '/machine-master': 'machines',
      '/machine-reservations': 'machines',
      '/add-machine-master': 'machines',
      '/machine-owners': 'machines',
      '/user-machine': 'machines',
      '/vendor/list': 'user',
      '/vendor/add': 'user',
      '/machineRegistration': 'machine',
      '/add-machine': 'machine',
      '/edit-machine': 'machine',
      '/view-machine': 'machine',
      '/multi-machine-registration': 'machine',
      
      // BOM & BOQ
      '/bom': 'bom',
      '/bom-list': 'bom',
      '/ai-bom': 'bom',
      '/boq': 'boq',
      '/boq/new': 'boq',
      '/ai-boq': 'boq',
      '/boq/edit': 'boq',
      '/boq/view': 'boq',
      '/road-boq-list': 'road boq',
      '/road-boq': 'road boq',
      '/road-boq/new': 'road boq',
      
      // Integrations
      '/integrations': 'integrations',
      '/integrations/clickup': 'integrations',
      '/integrations/primavera': 'integrations',
      '/integrations/microsoft-project': 'integrations',
      '/integrations/cad': 'integrations',
      '/cadviewer': 'cad viewer',
      
      // Financial Management
      '/financial': 'financial',
      '/project-bills': 'project bills',
      '/bills': 'bills',
      '/payment-list': 'payment',
      '/invoice': 'invoice',
      '/monthly-billing': 'monthly billing',
      '/add-monthly-billing': 'monthly billing',
      
      // Support & Tickets
      '/support-ticket': 'support ticket',
      '/support-ticket-list': 'support ticket',
      '/support-ticket/add': 'support ticket',
      '/support-ticket/edit': 'support ticket',
      '/support-ticket/view': 'support ticket',
      '/support-technical': 'support technical',
      '/support-technical/add': 'support technical',
      '/support-technical/edit': 'support technical',
      '/support-technical/view': 'support technical',
      '/contact-us': 'contact us',
      
      // Client & CRM
      '/clients': 'clients',
      '/lead-list': 'lead list',
      '/create-lead': 'leads',
      '/quotes-list': 'quotes list',
      '/create-quote': 'quotes',
      '/contact-list': 'contact list',
      '/create-contact': 'contact',
      
      // Documents & Reports
      '/documents': 'documents',
      '/document-list': 'documents',
      '/add-doc-project': 'documents',
      '/reports': 'reports',
      '/ai-reports': 'reports',
      '/progress-report': 'progress report',
      '/add-progress-report': 'progress report',
      '/plm/pavement': 'projects',
      '/plm/resources': 'projects',
      '/plm/closure': 'projects',
      '/plm/ops': 'projects',
      '/worklog': 'work log',
      
      // GIS & Mapping
      '/gis': 'gis',
      '/map': 'map',
      '/map-editor': 'map editor',
      '/map-editor-libre': 'map editor',
      '/map-editor-ol': 'gis',
      '/map-editor-ol-avi': 'gis',
      '/map-editor-nx': 'gis',
      '/gis-project': 'gis',
      '/geospatial': 'geospatial',
      '/home/geospatial': 'geospatial',
      '/geospatial-dashboard': 'geospatial',
      '/home/geospatial-dashboard': 'geospatial',
      
      // Labour Mobilization
      '/labour-mobilization/daily-reports': 'daily reports',
      '/labour-mobilization/daily-reports/add': 'daily reports',
      '/labour-mobilization/mobilization-plans': 'mobilization plans',
      '/labour-mobilization/mobilization-plans/add': 'mobilization plans',
      '/labour-mobilization/deviation-report': 'deviation report',
      
      // Alerts & Notifications
      '/alerts': 'alerts',
      
      // Tenders
      '/tender': 'tender',
      '/create-tender': 'tender',
      
      // Profile
      '/profile': 'profile',
      '/profile/personal-details': 'personal details',
      '/profile/contact-details': 'contact details',
      '/profile/password-manager': 'password manager',
      '/profile/identity-documents': 'identity documents',
      '/profile/notification-settings': 'notification settings'
    };

    // Remove leading /home if present and trailing slashes
    const normalizedRoute = route.replace(/^\/home/, '').replace(/\/$/, '');
    
    // Try exact match first
    if (routeMap[normalizedRoute]) {
      return routeMap[normalizedRoute];
    }
    
    // Try matching with / prefix
    if (routeMap['/' + normalizedRoute]) {
      return routeMap['/' + normalizedRoute];
    }
    
    // Try partial match (for routes with parameters)
    for (const routePattern in routeMap) {
      if (normalizedRoute.startsWith(routePattern.replace(/\/$/, ''))) {
        return routeMap[routePattern];
      }
    }
    
    // Fallback to extracting module from route
    const routeParts = normalizedRoute.split('/').filter(p => p && !/^\d+$/.test(p));
    return routeParts[0] || 'dashboard';
  }
}
